import { z } from 'zod';
// Funnel-leak diagnosis — Mag turns a confident funnel leak into a concrete,
// EVIDENCE-CITED recommendation. The evidence (behavioral signals at the leak
// step + the page content) is gathered deterministically and handed to the
// agent; the agent may only reason over what it's given. When there's nothing
// to ground a fix, it MUST return insufficient_evidence rather than invent one.
/** A STRUCTURAL recommendation — reordering the funnel itself (e.g. moving
 *  the payment gate earlier so commitment happens before a long pre-pay
 *  wizard). Only emitted when the funnel-intake semantics confirm the move
 *  is safe; always framed as a hypothesis the founder confirms. */
export const funnelStructuralDetailSchema = z.object({
    /** The concrete structural change — what moves where. Mag voice. */
    proposedChange: z.string().min(1).max(800),
    /** The funnel steps (event names) the reorder touches. */
    affectedSteps: z.array(z.string().min(1).max(120)).min(1).max(8),
    /** The founder-facing assumption the move rests on — surfaced so the
     *  founder can veto before any code change ("this assumes the wizard
     *  details aren't needed to price the order"). The requiredToComputePrice
     *  guardrail, in plain words. */
    assumptionToConfirm: z.string().min(1).max(500),
});
/** Where a leak's drop concentrates, broken down by an event property (e.g.
 *  which product the droppers selected). Present only when the deterministic
 *  segmentation found a lopsided breakdown; the numbers are drawn straight
 *  from that breakdown, not generated. */
export const funnelSegmentConcentrationSchema = z.object({
    /** The conversion_props key the drop is split by (e.g. 'wizard_type'). */
    propertyKey: z.string().min(1).max(120),
    /** The cited segments, worst drop first. */
    segments: z
        .array(z.object({
        value: z.string().min(1).max(200),
        /** 0..100 drop percentage for this value. */
        dropPct: z.number().min(0).max(100),
        droppers: z.number().int().min(0),
        /** Cohort size for this value (the denominator behind dropPct). */
        sampleSize: z.number().int().min(0),
    }))
        .min(2)
        .max(8),
});
export const funnelDiagnosisSchema = z.object({
    /** Observations, each a fact lifted from the evidence bundle (cite the
     *  numbers — "4 rage-clicks on /requests/new", "82% mobile"). Mag voice.
     *  Cap is 8 (not 6) to leave room for a segmentation fact alongside the
     *  behavioral ones. */
    observations: z.array(z.string().min(1).max(400)).min(1).max(8),
    /** What most likely causes the drop, tied to the observations above. */
    hypothesis: z.string().min(1).max(800),
    /** The concrete action to take. Generous cap — when the action is a deploy/
     *  instrumentation step it cites event facts and runs long. */
    recommendedAction: z.string().min(1).max(700),
    /** The check/experiment that would confirm it. */
    whatToTest: z.string().min(1).max(500),
    /** What the diagnosis rests on — so the founder knows its grounding. */
    evidenceBasis: z.enum(['behavioral', 'content', 'both']),
    /** What KIND of fix this is. 'copy' = page/CTA wording, 'instrumentation' =
     *  tracking to get signal, 'structural' = reorder the funnel itself. Defaults
     *  to 'copy' so pre-Phase-B cards parse unchanged. */
    recommendationType: z.enum(['copy', 'instrumentation', 'structural']).default('copy'),
    /** Present only when recommendationType === 'structural'. The reorder + the
     *  assumption it rests on. Absent for copy/instrumentation recs. */
    structuralDetail: funnelStructuralDetailSchema.nullish(),
    /** Present when the drop concentrates on specific values of an event
     *  property — the "where it concentrates" breakdown Mag cited. Absent for a
     *  uniform drop. Nullish so pre-existing cards parse unchanged. */
    segmentConcentration: funnelSegmentConcentrationSchema.nullish(),
});
export const funnelDiagnosisOutputSchema = z.discriminatedUnion('decision', [
    z.object({ decision: z.literal('diagnose'), diagnosis: funnelDiagnosisSchema }),
    z.object({
        decision: z.literal('insufficient_evidence'),
        /** Why there isn't enough to recommend a fix (honest, Mag voice). */
        reason: z.string().min(1).max(800),
        /** What to instrument / do to get the signal next. */
        whatToInstrument: z.string().min(1).max(800),
    }),
]);
// 'implemented' — founder shipped the fix. 'dismissed' — frictionless "stop
// surfacing this", no signal about the read itself. 'rejected' — founder is
// pushing back: the read/recommendation is wrong. The optional note carries
// their correction, which Mag reads as ground truth on the NEXT diagnosis so
// she steers off a faulty premise instead of just not-repeating.
export const funnelActionStatusSchema = z.enum(['implemented', 'dismissed', 'rejected']);
/** Founder verdict on a funnel-diagnosis card — recorded so Mag won't repeat
 *  a suggestion already acted on, and so a 'rejected' note can correct her read. */
export const setFunnelActionSchema = z.object({
    status: funnelActionStatusSchema,
    // Generous cap — founders paste in a full counter-analysis here (e.g. a
    // coding agent's investigation of why the read is an artifact), not a
    // one-liner. The DB column is unbounded `text`; this just guards abuse.
    note: z.string().max(8000).nullish(),
});
/** API-facing result: the leak that was diagnosed + the agent's output. */
export const brandFunnelDiagnosisSchema = z.object({
    leakStep: z.string(),
    prevStep: z.string(),
    dropOffPct: z.number().min(0).max(1),
    confidence: z.enum(['confident', 'tentative']),
    sampleSize: z.number().int().min(0),
    output: funnelDiagnosisOutputSchema,
    /** LLM cost of the diagnosis (already metered to the org via clientId). */
    costUsd: z.number().min(0),
});
//# sourceMappingURL=mag-funnel-diagnosis.js.map